import React, { useMemo } from "react"
import { Table, message, Tag, Typography } from "@mimber/ui-web"
import { useDonationPages } from "@mimber/client/mimber-organization"
import { DonationPageActionsDropdown } from "@/components/dropdowns"
import type { DonationPageDto } from "@mimber/schema"
import type { ColumnsType, TableProps } from "@mimber/ui-web"
import {
  applyQueryParamsToColumns,
  formatTableDate,
  getColumnSearchProps,
  handleTableChange,
} from "@mimber/ui-web"
import type { DonationPagesTableProps } from "./DonationPagesTable.types"
import { DONATION_PAGE_SEARCH_PLACEHOLDERS } from "./DonationPagesTable.utils"
import { useRememberedTableQueryParams } from "@/core/hooks"
import {
  getRememberedPaginationConfig,
  getRememberedTableLocale,
} from "@/utils"

const { Text } = Typography

export const DonationPagesTable: React.FunctionComponent<
  DonationPagesTableProps
> = ({ className, onRowClick, ...tableProps }) => {
  const { queryParams, setQueryParams, resetQueryParams } =
    useRememberedTableQueryParams({
      tableId: "donation-pages",
      defaultQueryParams: {
        page: 1,
        limit: 20,
      },
    })

  const { data: response, isLoading, error } = useDonationPages(queryParams)

  React.useEffect(() => {
    if (error) {
      message.error(`Failed to load donation pages: ${error.message}`)
    }
  }, [error])

  const donationPages = response?.data ?? []
  const total = response?.meta?.pagination?.total ?? 0

  const columns: ColumnsType<DonationPageDto> = useMemo(
    () => [
      {
        title: "Title",
        dataIndex: "title",
        key: "title",
        ...getColumnSearchProps(DONATION_PAGE_SEARCH_PLACEHOLDERS.title),
      },
      {
        title: "Slug",
        dataIndex: "slug",
        key: "slug",
        ...getColumnSearchProps(DONATION_PAGE_SEARCH_PLACEHOLDERS.slug),
        render: (slug: string) => <Text code>{slug}</Text>,
      },
      {
        title: "Currency",
        dataIndex: "currency",
        key: "currency",
      },
      {
        title: "Minimum",
        dataIndex: "minimumAmount",
        key: "minimumAmount",
        render: (minimumAmount: number) => (
          <Text>${(minimumAmount / 100).toFixed(2)}</Text>
        ),
      },
      {
        title: "Status",
        dataIndex: "isPublished",
        key: "isPublished",
        render: (isPublished: boolean) => (
          <Tag color={isPublished ? "success" : "default"}>
            {isPublished ? "Published" : "Draft"}
          </Tag>
        ),
      },
      {
        title: "Updated",
        dataIndex: "updatedAt",
        key: "updatedAt",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        render: (updatedAt: string) => formatTableDate(updatedAt),
      },
      {
        title: "Actions",
        key: "actions",
        width: 80,
        onCell: () => ({
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
        }),
        render: (_: unknown, record: DonationPageDto) => (
          <DonationPageActionsDropdown donationPage={record} />
        ),
      },
    ],
    []
  )

  const displayColumns = useMemo(
    () => applyQueryParamsToColumns(columns, queryParams),
    [columns, queryParams]
  )

  const onTableChange: TableProps<DonationPageDto>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    const tableChangeData = {
      pagination,
      filters: filters as Record<string, string[] | null>,
      sorter: {
        field: Array.isArray(sorter) ? sorter[0]?.field : sorter?.field,
        order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
      } as { field?: string | string[]; order?: "ascend" | "descend" },
    }

    handleTableChange(tableChangeData, setQueryParams, columns)
  }

  return (
    <>
      <Table<DonationPageDto>
        bordered
        columns={displayColumns}
        dataSource={donationPages}
        loading={isLoading}
        rowKey="uuid"
        pagination={getRememberedPaginationConfig(
          total,
          queryParams,
          resetQueryParams
        )}
        locale={getRememberedTableLocale(resetQueryParams, queryParams)}
        onChange={onTableChange}
        onRow={(record) => ({
          onClick: () => onRowClick?.(record),
          style: onRowClick ? { cursor: "pointer" } : undefined,
        })}
        className={className}
        {...tableProps}
      />
    </>
  )
}
